import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, EllipsisVerticalIcon, PhoneIcon, EnvelopeIcon, MapPinIcon, CalendarIcon, UserIcon, XMarkIcon, ChevronRightIcon, ChevronLeftIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { environment } from './environment';
import { useAuth } from './App';
import { renderFieldValue, formatFieldName } from './utils';

// Helper to get token from localStorage
function getToken() {
  return localStorage.getItem("token");
}

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sessions state
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  
  // Tab management
  const [activeTab, setActiveTab] = useState('');
  const [sessionTabs, setSessionTabs] = useState([]);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingSession, setSavingSession] = useState(false);
  const [editError, setEditError] = useState('');
  
  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deletingSession, setDeletingSession] = useState(false);
  

  
  // Success/Error messages
  const [sessionSuccess, setSessionSuccess] = useState('');
  const [sessionError, setSessionError] = useState('');
  
  // New Session Modal state
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [sessionForm, setSessionForm] = useState({
    instructions: ''
  });
  
  // Session creation state
  const [creatingSession, setCreatingSession] = useState(false);
  
  // EMR types and clients for dropdowns
  const [emrTypes, setEmrTypes] = useState([]);
  const [loadingEmrTypes, setLoadingEmrTypes] = useState(false);
  const [emrTypesError, setEmrTypesError] = useState('');
  
  // EMR Fields for dropdown values
  const [emrFields, setEmrFields] = useState([]);
  const [loadingEmrFields, setLoadingEmrFields] = useState(false);
  const [emrFieldsError, setEmrFieldsError] = useState('');
  
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState('');

  // EMR Type Results for dynamic field generation
  const [emrTypeResults, setEmrTypeResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState('');

  // Manual Fields from API
  const [manualFields, setManualFields] = useState([]);
  const [loadingManualFields, setLoadingManualFields] = useState(false);
  const [manualFieldsError, setManualFieldsError] = useState('');

  // Fields to Display feature state (separate from main Sessions)
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [visibleFields, setVisibleFields] = useState([]);
  const [selectedAvailableField, setSelectedAvailableField] = useState(null);
  const [selectedVisibleField, setSelectedVisibleField] = useState(null);
  const [clientFieldDisplayConfig, setClientFieldDisplayConfig] = useState({});

  // Helper function to get token
  function getToken() {
    return localStorage.getItem("token");
  }

  useEffect(() => {
    console.log('ClientDetail useEffect triggered, clientId:', clientId);
    fetchClient();
    fetchClientSessions();
    fetchEmrTypes();
    fetchEmrFields();
    fetchClients();
    fetchEmrTypeResults();
    fetchManualFields();
  }, [clientId]);

  // Add refresh on focus to catch updates from other pages
  useEffect(() => {
    const handleFocus = () => {
      fetchClient();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Load client field display configuration from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('clientFieldDisplayConfig');
    if (savedConfig) {
      try {
        setClientFieldDisplayConfig(JSON.parse(savedConfig));
      } catch (error) {
        console.error('Error parsing client field display config:', error);
      }
    }
  }, []);

  // Auto-hide success message after 2 seconds
  useEffect(() => {
    if (sessionSuccess) {
      const timer = setTimeout(() => {
        setSessionSuccess('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionSuccess]);

  const fetchClient = async () => {
    console.log('fetchClient called, clientId:', clientId);
    console.log('API URL:', `${environment.apiUrl}/Clients/${clientId}`);
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${environment.apiUrl}/api/Clients/${clientId}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      console.log('API response status:', res.status);
      const data = await res.json();
      console.log('API response data:', data);
      console.log('Client history field:', data.history);
      if (!res.ok) throw new Error('Failed to fetch client');
      setClient(data);
    } catch (err) {
      console.error('Error fetching client:', err);
      setError('Could not load client data from the server.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch sessions for this specific client
  const fetchClientSessions = async () => {
    setLoadingSessions(true);
    setSessionsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/sessions?client_id=${clientId}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Client sessions fetched:', data);
        setSessions(data);
        
        // Create tabs based on unique EMR types
        const emrNames = data.map(session => safeString(session.emr_name)).filter(type => type !== 'Unknown Type');
        const uniqueTypes = emrNames.filter((type, index, self) => 
          self.findIndex(t => t.toLowerCase().trim() === type.toLowerCase().trim()) === index
        );
        console.log('Unique session types for tabs:', uniqueTypes);
        setSessionTabs(uniqueTypes);
        
        // Set first tab as active if no active tab
        if (uniqueTypes.length > 0 && !activeTab) {
          setActiveTab(uniqueTypes[0]);
        }
      } else {
        setSessionsError('Failed to fetch sessions');
      }
    } catch (err) {
      console.error('Network error fetching client sessions:', err);
      setSessionsError('Network error');
    } finally {
      setLoadingSessions(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      // Check if it's a date-only string (YYYY-MM-DD) or datetime string
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Date-only string like "2025-06-01" - add time to avoid timezone issues
        date = new Date(dateString + 'T00:00:00');
      } else {
        // DateTime string like "2025-06-27 15:41:55" - parse directly
        date = new Date(dateString);
      }
      
      if (isNaN(date)) return dateString;
      
      // Check if the date has time components (not just date)
      const hasTime = dateString.includes(' ') || dateString.includes('T');
      
      if (hasTime) {
        // For datetime strings, include time with 12-hour format
        const formattedDate = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        return formattedDate.replace(' at ', ' ');
      } else {
        // For date-only strings, just show the date
        const formattedDate = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        return formattedDate;
      }
    } catch (error) {
      return dateString;
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName?.[0] || '').toUpperCase()}${(lastName?.[0] || '').toUpperCase()}`;
  };

  // Safe string helper for tab names
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      console.warn('Tab name is an object:', value);
      return 'Unknown Type';
    }
    return 'Unknown Type';
  };

  // Safe render helper function
  const safeRender = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
      console.warn('Attempted to render object:', value);
      if (value.msg) return value.msg;
      if (value.message) return value.message;
      if (value.error) return value.error;
      return fallback;
    }
    return fallback;
  };

  // Helper function to render field based on type
  const renderFieldByType = (fieldName, fieldValue, fieldType, onChangeHandler = null) => {
    // Use provided onChangeHandler or default to setEditForm
    const handleChange = onChangeHandler || ((e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.value })));
    const handleCheckboxChange = onChangeHandler || ((e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.checked })));
    
    switch(fieldType) {
      case 'date':
        return (
          <input
            type="date"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'time':
        return (
          <input
            type="time"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'boolean':
        const booleanValue = typeof fieldValue === 'string' 
          ? fieldValue.toLowerCase() === 'true'
          : Boolean(fieldValue);
        return (
          <input
            type="checkbox"
            id={`field_${fieldName}`}
            checked={booleanValue}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
        );
      case 'textarea':
        return (
          <textarea
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="2"
            placeholder={`Enter ${fieldName.toLowerCase()}`}
          />
        );
      case 'dropdown':
        // Get dropdown values from EMR field definitions
        const dropdownField = emrFields?.find(field => 
          field.name.toLowerCase() === fieldName.toLowerCase() && 
          field.type === 'dropdown'
        );
        const dropdownOptions = dropdownField?.dropdown_values?.split('\n').filter(val => val.trim()) || [];
        
        return (
          <select
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select an option</option>
            {dropdownOptions.map((option, index) => (
              <option key={index} value={option.trim()}>
                {option.trim()}
              </option>
            ))}
          </select>
        );
      case 'text':
      default:
        return (
          <input
            type="text"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={`Enter ${fieldName.toLowerCase()}`}
          />
        );
    }
  };

  // Get sessions for a specific type
  const getSessionsByType = (typeName) => {
    return sessions.filter(session => session.emr_name === typeName);
  };

  // Fields to Display functionality for Client Sessions
  const openFieldsModal = () => {
    if (!activeTab) return;
    
    // Get the EMR type ID for the active tab
    const activeEmrType = emrTypes.find(type => type.name === activeTab);
    if (!activeEmrType) {
      console.warn('EMR type not found for active tab:', activeTab);
      return;
    }
    
    // Get all available fields for this EMR type
    const allFields = [];
    if (emrTypeResults && Array.isArray(emrTypeResults)) {
      const confirmedFields = emrTypeResults.filter(result => 
        result.emr_type_id === activeEmrType.id && result.status === "confirmed"
      );
      allFields.push(...confirmedFields.map(result => result.key));
    }
    
    // Add manual fields for this EMR type
    const typeManualFields = getManualFieldsForType(activeEmrType.id);
    if (typeManualFields.length > 0) {
      allFields.push(...typeManualFields.map(field => field.name));
    }
    
    // Add common fields that are always available
    const commonFields = ['Type', 'Client', 'Instructions', 'Created At'];
    allFields.push(...commonFields);
    
    // Get current visible fields from config or use defaults
    const currentConfig = clientFieldDisplayConfig[activeTab] || ['Type', 'Client', 'Instructions', 'Created At'];
    
    // Set available fields (all fields minus visible fields)
    const available = allFields.filter(field => !currentConfig.includes(field));
    
    console.log('=== CLIENT FIELDS MODAL DEBUG ===');
    console.log('Active tab:', activeTab);
    console.log('Active EMR type:', activeEmrType);
    console.log('All fields:', allFields);
    console.log('Manual fields:', typeManualFields);
    console.log('Current config:', currentConfig);
    console.log('Available fields:', available);
    console.log('================================');
    
    setAvailableFields(available);
    setVisibleFields(currentConfig);
    setSelectedAvailableField(null);
    setSelectedVisibleField(null);
    setShowFieldsModal(true);
  };

  const moveFieldToVisible = () => {
    if (!selectedAvailableField) return;
    
    setVisibleFields(prev => [...prev, selectedAvailableField]);
    setAvailableFields(prev => prev.filter(field => field !== selectedAvailableField));
    setSelectedAvailableField(null);
  };

  const moveFieldToAvailable = () => {
    if (!selectedVisibleField) return;
    
    setAvailableFields(prev => [...prev, selectedVisibleField]);
    setVisibleFields(prev => prev.filter(field => field !== selectedVisibleField));
    setSelectedVisibleField(null);
  };

  const moveFieldUp = () => {
    if (!selectedVisibleField) return;
    
    const currentIndex = visibleFields.indexOf(selectedVisibleField);
    if (currentIndex <= 0) return;
    
    const newVisibleFields = [...visibleFields];
    [newVisibleFields[currentIndex], newVisibleFields[currentIndex - 1]] = 
    [newVisibleFields[currentIndex - 1], newVisibleFields[currentIndex]];
    
    setVisibleFields(newVisibleFields);
  };

  const moveFieldDown = () => {
    if (!selectedVisibleField) return;
    
    const currentIndex = visibleFields.indexOf(selectedVisibleField);
    if (currentIndex >= visibleFields.length - 1) return;
    
    const newVisibleFields = [...visibleFields];
    [newVisibleFields[currentIndex], newVisibleFields[currentIndex + 1]] = 
    [newVisibleFields[currentIndex + 1], newVisibleFields[currentIndex]];
    
    setVisibleFields(newVisibleFields);
  };

  const saveFieldDisplayConfig = () => {
    const newConfig = {
      ...clientFieldDisplayConfig,
      [activeTab]: visibleFields
    };
    setClientFieldDisplayConfig(newConfig);
    
    // Save to localStorage with separate key for client sessions
    localStorage.setItem('clientFieldDisplayConfig', JSON.stringify(newConfig));
    
    setShowFieldsModal(false);
  };

  const getVisibleFieldsForCurrentTab = () => {
    return clientFieldDisplayConfig[activeTab] || ['Type', 'Client', 'Instructions', 'Created At'];
  };

  const renderFieldValueForTable = (session, fieldName) => {
    const value = renderFieldValue(session, fieldName, formatDate);
    
    switch (fieldName) {
      case 'Type':
        return (
          <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-md text-xs font-semibold">
            {safeRender(value)}
          </span>
        );
      default:
        return safeRender(value);
    }
  };

  // Handle Edit Session
  const handleEditSession = (session) => {
    console.log('=== EDIT SESSION DEBUG (ClientDetail) ===');
    console.log('Session data received:', session);
    console.log('session.client_id_name:', session.client_id_name);
    console.log('session.client field:', session.client);
    console.log('All session keys:', Object.keys(session));
    console.log('All session values:', Object.entries(session));
    setEditingSession(session);
    setEditError('');
    
    // Pre-fill the edit form with current session data
    const editFormData = {
      instructions: session.manual_instructions || ''
    };
    
    // Get all available fields for this EMR type (dynamic + manual)
    const availableFields = [];
    
    // Get dynamic fields from emrTypeResults
    if (emrTypeResults && Array.isArray(emrTypeResults)) {
      const confirmedFields = emrTypeResults.filter(result => 
        result.emr_type_id === session.emr_type_id && result.status === "confirmed"
      );
      // Get unique field names to avoid duplicates (case-insensitive)
      const fieldNames = confirmedFields.map(result => result.key);
      const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
        self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
      );
      availableFields.push(...uniqueFieldNames);
    }
    
    // Get manual fields
    const typeManualFields = getManualFieldsForType(session.emr_type_id);
    if (typeManualFields.length > 0) {
      availableFields.push(...typeManualFields.map(field => field.name));
    }
    
    console.log('Available fields for this EMR type:', availableFields);
    
    // Add all available fields for this EMR type, with existing values or empty strings
    availableFields.forEach(fieldName => {
      // Try different possible field name variations
      const variations = [
        fieldName, // Original field name (e.g., "appt date")
        fieldName.replace(/\s+/g, '_'), // Replace spaces with underscores (e.g., "appt_date")
        fieldName.replace(/\s+/g, ''), // Remove spaces (e.g., "apptdate")
        fieldName.toLowerCase().replace(/\s+/g, '_'), // Lowercase with underscores (e.g., "appt_date")
        fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(''), // PascalCase (e.g., "ApptDate")
        fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('_'), // PascalCase with underscores (e.g., "Appt_Date")
        // Additional variations for common field name patterns
        fieldName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''), // Clean snake_case
        fieldName.replace(/\s+/g, '_').toLowerCase(), // Simple space to underscore conversion
      ];
      
      console.log(`Trying variations for field "${fieldName}":`, variations);
      
      // Find which variation exists in the session data
      let foundValue = null;
      
      // Special handling for client_id field - look for client__id (double underscore) instead
      if (fieldName === 'client id' || fieldName === 'client_id') {
        if (session.hasOwnProperty('client__id') && session.client__id !== null && session.client__id !== undefined && session.client__id !== '') {
          foundValue = session.client__id;
          console.log(`Found client_id field as "client__id" with value: "${foundValue}"`);
        } else {
          // If client__id is empty/null/undefined, don't show anything (not the UUID)
          foundValue = '';
          console.log(`client__id field is empty, setting to empty string`);
        }
      } else {
        // Normal field lookup for all other fields
      for (const variation of variations) {
        console.log(`Checking variation "${variation}" for field "${fieldName}"`);
        console.log(`session.hasOwnProperty("${variation}"):`, session.hasOwnProperty(variation));
        console.log(`session["${variation}"]:`, session[variation]);
        
        if (session.hasOwnProperty(variation) && session[variation] !== null && session[variation] !== undefined) {
          foundValue = session[variation];
          console.log(`Found field "${fieldName}" as "${variation}" with value: "${foundValue}"`);
          break;
          }
        }
      }
      
      // If not found with variations, try case-insensitive search through all session keys
      if (foundValue === null) {
        console.log(`Trying case-insensitive search for field "${fieldName}"`);
        const sessionKeys = Object.keys(session);
        for (const key of sessionKeys) {
          const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
          const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (normalizedKey === normalizedFieldName) {
            foundValue = session[key];
            console.log(`Found field "${fieldName}" as "${key}" (case-insensitive match) with value: "${foundValue}"`);
            break;
          }
        }
      }
      
      if (foundValue === null) {
        console.log(`Field "${fieldName}" not found in session data with any variation`);
      }
      
      // Get field type to set appropriate default value
      let fieldType = 'text';
      
      // First try to find in EMR field definitions
      const emrField = emrFields.find(field => 
        field.name.toLowerCase() === fieldName.toLowerCase()
      );
      if (emrField) {
        fieldType = emrField.type;
      } else {
        // Try to find in manual fields
        const manualField = getManualFieldsForType(session.emr_type_id).find(field => 
          field.name.toLowerCase() === fieldName.toLowerCase()
        );
        if (manualField) {
          fieldType = manualField.type;
        } else {
          // Fallback to emrTypeResults
          fieldType = emrTypeResults.find(result => 
            result.emr_type_id === session.emr_type_id && 
            result.status === "confirmed" && 
            result.key === fieldName
          )?.type || 'text';
        }
      }
      
      // Set appropriate default value based on field type
      switch(fieldType) {
        case 'date':
          editFormData[fieldName] = foundValue || null;  // null for empty date
          break;
        case 'time':
          editFormData[fieldName] = foundValue || null;  // null for empty time
          break;
        case 'datetime':
          editFormData[fieldName] = foundValue || null;  // null for empty datetime
          break;
        case 'boolean':
          editFormData[fieldName] = foundValue !== null && foundValue !== undefined ? foundValue : false;  // false for empty boolean
          break;
        case 'text':
        case 'textarea':
        default:
          editFormData[fieldName] = foundValue || '';  // empty string for empty text
          break;
      }
    });
    
    console.log('Edit form data being set:', editFormData);
    console.log('All fields in edit form:', Object.keys(editFormData));
    setEditForm(editFormData);
    setShowEditModal(true);
  };

  // Handle View Session Details
  const handleViewSessionDetails = (session) => {
    navigate(`/sessions/${session.id}`, {
      state: {
        from: `/Clients/${clientId}`,
        clientId: clientId
      }
    });
  };

  // Handle Save Session Changes
  const handleSaveSessionChanges = async () => {
    if (!editingSession) return;
    
    setSavingSession(true);
    setEditError('');
    
    try {
      const updateData = {};
      
      // Add manual_instructions field (common for all types)
      updateData.manual_instructions = editForm.instructions || '';
      
      // Get field types from emrTypeResults and manual fields
      const fieldTypes = {};
      
      // Get dynamic field types from emrTypeResults
      if (emrTypeResults && Array.isArray(emrTypeResults)) {
        const confirmedFields = emrTypeResults.filter(result => 
          result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
        );
        confirmedFields.forEach(field => {
          fieldTypes[field.key] = field.type;
        });
      }
      
      // Get manual field types
      const typeManualFields = getManualFieldsForType(editingSession.emr_type_id);
      typeManualFields.forEach(field => {
        fieldTypes[field.name] = field.type;
      });
      
      // Add all dynamic fields from the edit form with proper type handling
      // Only send the original field names from emrTypeResults (UI display names)
      Object.keys(editForm).forEach(fieldName => {
        // Skip instructions field as it's handled separately above
        if (fieldName === 'instructions') return;
        
        const fieldType = fieldTypes[fieldName] || 'text';
        const fieldValue = editForm[fieldName];
        
        // Handle different field types according to backend requirements
        switch(fieldType) {
          case 'text':
            updateData[fieldName] = fieldValue || '';  // Empty string for empty text
            break;
          case 'date':
            if (fieldValue) {
              // Convert date string to ISO date format (YYYY-MM-DD)
              const date = new Date(fieldValue);
              updateData[fieldName] = date.toISOString().split('T')[0];
            } else {
              updateData[fieldName] = null;  // null for empty date
            }
            break;
          case 'time':
            updateData[fieldName] = fieldValue || null; // null for empty time
            break;
          case 'datetime':
            if (fieldValue) {
              // Convert datetime-local string to timestamp without timezone conversion
              // Use the local time as-is to avoid timezone issues
              const date = new Date(fieldValue);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              updateData[fieldName] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            } else {
              updateData[fieldName] = null;  // null for empty date
            }
            break;
          case 'boolean':
            updateData[fieldName] = fieldValue ? 'true' : 'false';  // true/false for boolean
            break;
          case 'textarea':
            updateData[fieldName] = fieldValue || '';  // Empty string for empty textarea
            break;
          default:
            updateData[fieldName] = fieldValue || '';
        }
      });

      console.log('Updating session with data:', updateData);
      console.log('Original session client_id_name:', editingSession.client_id_name);
      console.log('Original session client field:', editingSession.client);
      console.log('Edit form client field:', editForm.client);
      
      const response = await fetch(`${environment.apiUrl}/sessions/${editingSession.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const updatedSession = await response.json();
        console.log('Session updated successfully:', updatedSession);
        console.log('Updated session client_id_name:', updatedSession.client_id_name);
        console.log('Updated session client field:', updatedSession.client);
        
        // Refresh the sessions list to get the updated data
        await fetchClientSessions();
        
        // Close modal and show success
        setShowEditModal(false);
        setEditingSession(null);
        setEditForm({});
        setSessionSuccess('Session updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update session:', errorData);
        setEditError(errorData.detail || 'Failed to update session');
      }
    } catch (err) {
      console.error('Network error updating session:', err);
      setEditError('Network error updating session');
    } finally {
      setSavingSession(false);
    }
  };

  // Handle Delete Session
  const handleDeleteSession = (session) => {
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  // Confirm Delete Session
  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    
    setDeletingSession(true);
    try {
      const response = await fetch(`${environment.apiUrl}/sessions/${sessionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        setSessionSuccess('Session deleted successfully!');
        setShowDeleteModal(false);
        setSessionToDelete(null);
        // Refresh the sessions list
        await fetchClientSessions();
      } else {
        setSessionError('Failed to delete session');
      }
    } catch (err) {
      setSessionError('Network error deleting session');
    } finally {
      setDeletingSession(false);
    }
  };

  // Fetch EMR Types
  const fetchEmrTypes = async () => {
    setLoadingEmrTypes(true);
    setEmrTypesError('');
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('EMR Types fetched:', data);
        setEmrTypes(data);
      } else {
        setEmrTypesError('Failed to fetch EMR types');
      }
    } catch (err) {
      setEmrTypesError('Network error');
    } finally {
      setLoadingEmrTypes(false);
    }
  };

  // Fetch EMR Fields (for dropdown values)
  const fetchEmrFields = async () => {
    setLoadingEmrFields(true);
    setEmrFieldsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('EMR Fields fetched:', data);
        setEmrFields(data);
      } else {
        setEmrFieldsError('Failed to fetch EMR fields');
      }
    } catch (err) {
      setEmrFieldsError('Network error');
    } finally {
      setLoadingEmrFields(false);
    }
  };

  // Fetch EMR Type Results for dynamic field generation
  const fetchEmrTypeResults = async () => {
    setLoadingResults(true);
    setResultsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/results`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEmrTypeResults(data);
      } else {
        setResultsError('Failed to fetch EMR type results');
      }
    } catch (err) {
      setResultsError('Network error');
    } finally {
      setLoadingResults(false);
    }
  };

  // Fetch Manual Fields
  const fetchManualFields = async () => {
    setLoadingManualFields(true);
    setManualFieldsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/manual-fields/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Manual fields fetched:', data);
        setManualFields(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch manual fields:', response.status, errorData);
        setManualFieldsError('Failed to fetch manual fields');
      }
    } catch (err) {
      console.error('Network error fetching manual fields:', err);
      setManualFieldsError('Network error: ' + err.message);
    } finally {
      setLoadingManualFields(false);
    }
  };

  // Fetch Clients
  const fetchClients = async () => {
    setLoadingClients(true);
    setClientsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/api/Clients`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Clients fetched:', data);
        setClients(data);
      } else {
        setClientsError('Failed to fetch clients');
      }
    } catch (err) {
      setClientsError('Network error');
    } finally {
      setLoadingClients(false);
    }
  };

  // Handle New Session Modal
  const handleNewSessionClick = () => {
    setSelectedClient(clientId); // Pre-fill with current client
    setSelectedType('');
    setSessionForm({
      instructions: ''
    });
    setShowNewSessionModal(true);
  };

  // Handle Type Change
  const handleTypeChange = (emrTypeId) => {
    console.log('Type changed to:', emrTypeId);
    setSelectedType(emrTypeId);
    // Reset form when type changes - only keep instructions
    setSessionForm({
      instructions: ''
    });
  };

  // Handle Form Change
  const handleFormChange = (field, value) => {
    setSessionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get manual fields for a specific EMR type
  const getManualFieldsForType = (emrTypeId) => {
    if (!emrTypeId || !manualFields || !Array.isArray(manualFields)) {
      return [];
    }
    
    // Filter manual fields by emr_type_id
    const typeManualFields = manualFields.filter(field => 
      field.emr_type_id === emrTypeId
    );
    
    console.log('Manual fields for EMR type:', emrTypeId, typeManualFields);
    return typeManualFields;
  };



  // Handle Create Session
  const handleCreateSession = async () => {
    // Validate required fields
    if (!selectedType || !selectedClient) {
      setSessionError('Please select both Type and Client before creating a session.');
      return;
    }

    setCreatingSession(true);
    setSessionError('');
    setSessionSuccess('');

    try {
      const sessionData = {
        emr_type_id: selectedType,
        client_id: selectedClient,
        manual_instructions: sessionForm.instructions || '',
      };

      // Add dynamic fields from confirmed results
      if (emrTypeResults && Array.isArray(emrTypeResults)) {
        const confirmedFields = emrTypeResults.filter(result => 
          result.emr_type_id === selectedType && result.status === "confirmed"
        );
        
        console.log('Adding dynamic fields to session data:', confirmedFields);
        
        confirmedFields.forEach(result => {
          const fieldName = result.key;
          const fieldValue = sessionForm[fieldName];
          
          // Skip client field as it's handled by client_id
          if (fieldName === 'client' || fieldName === 'client_id' || fieldName === 'client id') {
            return;
          }
          
          // Add field to session data - send ALL confirmed fields even if empty
          if (fieldValue !== undefined && fieldValue !== null) {
            // Handle different field types for empty values
            const fieldType = result.type || 'text';
            switch(fieldType) {
              case 'date':
              case 'time':
              case 'datetime':
                sessionData[fieldName] = fieldValue || null; // null for empty date/time/datetime
                break;
              case 'boolean':
                sessionData[fieldName] = fieldValue || false; // false for empty booleans
                break;
              case 'text':
              case 'textarea':
              default:
                sessionData[fieldName] = fieldValue || ''; // empty string for empty text
                break;
            }
          } else {
            // Field value is undefined/null, set appropriate default based on type
            const fieldType = result.type || 'text';
            switch(fieldType) {
              case 'date':
              case 'time':
              case 'datetime':
                sessionData[fieldName] = null;
                break;
              case 'boolean':
                sessionData[fieldName] = false;
                break;
              case 'text':
              case 'textarea':
              default:
                sessionData[fieldName] = '';
                break;
            }
          }
        });
      }

      // Add manual fields
      const typeManualFields = getManualFieldsForType(selectedType);
      if (typeManualFields.length > 0) {
        console.log('Adding manual fields to session data:', typeManualFields);
        
        typeManualFields.forEach(field => {
          const fieldName = field.name;
          const fieldValue = sessionForm[fieldName];
          
          // Add field to session data - send ALL manual fields even if empty
          if (fieldValue !== undefined && fieldValue !== null) {
            // Handle different field types for empty values
            const fieldType = field.type || 'text';
            switch(fieldType) {
              case 'date':
              case 'time':
              case 'datetime':
                sessionData[fieldName] = fieldValue || null; // null for empty date/time/datetime
                break;
              case 'boolean':
                sessionData[fieldName] = fieldValue || false; // false for empty booleans
                break;
              case 'text':
              case 'textarea':
              default:
                sessionData[fieldName] = fieldValue || ''; // empty string for empty text
                break;
            }
          } else {
            // Field value is undefined/null, set appropriate default based on type
            const fieldType = field.type || 'text';
            switch(fieldType) {
              case 'date':
              case 'time':
              case 'datetime':
                sessionData[fieldName] = null;
                break;
              case 'boolean':
                sessionData[fieldName] = false;
                break;
              case 'text':
              case 'textarea':
              default:
                sessionData[fieldName] = '';
                break;
            }
          }
        });
      }

      console.log('Creating session with data:', sessionData);
      
      const response = await fetch(`${environment.apiUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(sessionData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Session created successfully:', data);
        setSessionSuccess('Session created successfully!');
        setShowNewSessionModal(false);
        setSelectedType('');
        setSelectedClient('');
        setSessionForm({
          instructions: ''
        });
        
        // Refresh sessions and update tabs
        await fetchClientSessions();
        
        // Redirect to the newly created session
        if (data && data.id) {
          navigate(`/sessions/${data.id}`, {
            state: {
              from: `/Clients/${clientId}`,
              clientId: clientId,
              isNewSession: true
            }
          });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to create session:', errorData);
        setSessionError(errorData.detail || 'Failed to create session');
      }
    } catch (err) {
      console.error('Network error creating session:', err);
      setSessionError('Network error creating session');
    } finally {
      setCreatingSession(false);
    }
  };

  // Check if form is valid
  const isFormValid = selectedType && selectedClient;

  if (loading) {
    return (
      <div className="w-full flex flex-col items-center justify-center">
        <div className="text-lg text-slate-700">Loading client data...</div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="w-full flex flex-col items-center justify-center">
        <div className="text-lg text-red-600">{error || 'Client not found'}</div>
        <button
          onClick={() => navigate('/Clients')}
          className="mt-4 flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/Clients')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Clients
        </button>
        <button
          onClick={fetchClient}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors px-3 py-1 rounded-lg hover:bg-slate-100"
          title="Refresh client data"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Client Information Card */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-slate-200 rounded-xl p-6 mb-6 shadow-lg">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-extrabold text-2xl shadow-lg">
            {getInitials(client.first_name, client.last_name)}
          </div>
          
          {/* Client Details */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900 mb-1">
              {client.first_name} {client.last_name}
            </h1>
            <p className="text-slate-500 text-base mb-4">Client Information</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-5 border border-slate-100">
                <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                  Personal Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Date of Birth</p>
                      <p className="text-slate-500 font-normal">{formatDate(client.date_of_birth)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <PhoneIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Phone Number</p>
                      <p className="text-slate-500 font-normal">{client.phone || ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <EnvelopeIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Email Address</p>
                      <p className="text-slate-500 font-normal">{client.email || ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <MapPinIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Home Address</p>
                      <p className="text-slate-500 font-normal">{client.address || ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 text-black mt-1">📋</div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-900">History</p>
                      <p className="text-slate-500 font-normal whitespace-pre-wrap">
                        {client.history || 'No history recorded'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Collateral Information */}
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-5 border border-slate-100">
                <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                  Collateral Contact
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <UserIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">First Name</p>
                      <p className="text-slate-500 font-normal">{client.collateral_first_name || ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <UserIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Last Name</p>
                      <p className="text-slate-500 font-normal">{client.collateral_last_name || ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <EnvelopeIcon className="w-6 h-6 text-black" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Email</p>
                      <p className="text-slate-500 font-normal">{client.collateral_email || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {sessionSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{sessionSuccess}</p>
            </div>
          </div>
        </div>
      )}
      {sessionError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{sessionError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-700">Sessions</h2>
          <button
            onClick={handleNewSessionClick}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            New Session
          </button>
        </div>

        {/* Loading State */}
        {loadingSessions && (
          <div className="text-center py-8">
            <div className="text-lg text-slate-700">Loading sessions...</div>
          </div>
        )}

        {/* Error State */}
        {sessionsError && (
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">Error loading sessions: {sessionsError}</div>
            <button
              onClick={fetchClientSessions}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Sessions Tabs */}
        {!loadingSessions && !sessionsError && sessionTabs.length > 0 && sessions && Array.isArray(sessions) ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Tab Navigation */}
            <div className="border-b border-slate-200 bg-slate-50">
              <div className="flex space-x-1 px-6">
                {sessionTabs.map((tabName) => (
                  <button
                    key={tabName}
                    onClick={() => setActiveTab(tabName)}
                    className={`px-6 py-4 text-sm font-medium rounded-t-lg transition-all duration-200 ${
                      activeTab === tabName
                        ? 'bg-white text-blue-700 border-b-2 border-blue-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                    }`}
                  >
                    {tabName}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-slate-800">{activeTab} Sessions</h3>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                        {getSessionsByType(activeTab).length} session{getSessionsByType(activeTab).length !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={openFieldsModal}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200 group relative"
                        title="Fields to Display"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Fields to Display
                        </span>
                      </button>
                    </div>
                  </div>
                  {getSessionsByType(activeTab).length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto h-12 w-12 text-slate-400 mb-4">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">No {activeTab} sessions found</h3>
                      <p className="text-slate-500">Create your first {activeTab} session using the "New Session" button above.</p>
                    </div>
                  ) : (
        <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                          <tr>
                            {getVisibleFieldsForCurrentTab().map((fieldName) => (
                              <th key={fieldName} className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                {fieldName}
                              </th>
                            ))}
                            <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                          {getSessionsByType(activeTab).map((session, index) => (
                            <tr 
                              key={session.id} 
                              className={`hover:bg-blue-100 transition-colors duration-150 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                              onClick={() => handleViewSessionDetails(session)}
                            >
                              {getVisibleFieldsForCurrentTab().map((fieldName) => (
                                <td key={fieldName} className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                                  {renderFieldValueForTable(session, fieldName)}
                                </td>
                              ))}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                <div className="flex gap-2">
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditSession(session);
                                    }}
                                    className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit Session"
                                  >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSession(session);
                                    }}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    title="Delete Session"
                                  >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : !loadingSessions && !sessionsError ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="text-center text-slate-500 py-8">
              No sessions found for this client. Create your first session using the "New Session" button above.
            </div>
          </div>
        ) : null}
        </div>

      {/* Edit Session Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 pt-8">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] m-4 overflow-hidden">
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 4rem)' }}>
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-slate-700">Edit Session</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSession(null);
                    setEditForm({});
                  }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Session Info - Only show if there are confirmed fields */}
              {(() => {
                const availableFields = [];
                if (emrTypeResults && Array.isArray(emrTypeResults)) {
                  const confirmedFields = emrTypeResults.filter(result => 
                    result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
                  );
                  const fieldNames = confirmedFields.map(result => result.key);
                  const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
                    self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
                  );
                  availableFields.push(...uniqueFieldNames);
                }
                
                // Only show Session Info if there are confirmed fields
                if (availableFields.length === 0) {
                  return null;
                }
                
                return (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                      <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Session Information
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                  </div>
                  <div>
                            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Type</span>
                            <p className="text-sm font-semibold text-slate-900">{editingSession?.emr_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                  </div>
                  <div>
                            <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Client</span>
                            <p className="text-sm font-semibold text-slate-900">{editingSession?.client_id_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                  </div>
                  <div>
                            <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">Created</span>
                            <p className="text-sm font-semibold text-slate-900">
                              {editingSession?.created_at ? (() => {
                                const formattedDate = new Date(editingSession.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                });
                                return formattedDate.replace(' at ', ' ');
                              })() : '—'}
                            </p>
                  </div>
                </div>
              </div>
                    </div>
                  </div>
                );
              })()}

              {/* Edit Form */}
              <div className="space-y-4">
                {/* Dynamic Fields - Show all available fields for this EMR type */}
                {editingSession && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="text-lg font-medium text-slate-700">Session Fields</h4>
                    {/* Get all available fields for this EMR type */}
                    {(() => {
                      const availableFields = [];
                      
                      // Get dynamic fields from emrTypeResults
                      if (emrTypeResults && Array.isArray(emrTypeResults)) {
                        const confirmedFields = emrTypeResults.filter(result => 
                          result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
                        );
                        // Get unique field names to avoid duplicates (case-insensitive)
                        const fieldNames = confirmedFields.map(result => result.key);
                        const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
                          self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
                        );
                        availableFields.push(...uniqueFieldNames);
                      }
                      
                      // Get manual fields
                      const typeManualFields = getManualFieldsForType(editingSession.emr_type_id);
                      if (typeManualFields.length > 0) {
                        availableFields.push(...typeManualFields.map(field => field.name));
                      }
                      
                      console.log('Available fields for this EMR type in edit modal:', availableFields);
                      
                      // Show message if no confirmed fields available
                      if (availableFields.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                              <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-sm">
                              <h3 className="text-xl font-bold text-amber-900 mb-3">No confirmed fields available</h3>
                              <p className="text-amber-700 text-base leading-relaxed">
                                You don't have any confirmed fields in the results table yet. 
                                <br />
                                <span className="text-sm text-amber-600 mt-2 block">
                                  Fields will appear here once they are confirmed in the EMR type analysis.
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      }
                      
                      return availableFields.map((fieldName) => {
                        // Get field type from EMR field definitions first, then fallback to manual fields or emrTypeResults
                        let fieldType = 'text';
                        
                        // First try to find in EMR field definitions
                        const emrField = emrFields.find(field => 
                          field.name.toLowerCase() === fieldName.toLowerCase()
                        );
                        if (emrField) {
                          fieldType = emrField.type;
                        } else {
                          // Try to find in manual fields
                          const manualField = getManualFieldsForType(editingSession.emr_type_id).find(field => 
                            field.name.toLowerCase() === fieldName.toLowerCase()
                          );
                          if (manualField) {
                            fieldType = manualField.type;
                          } else {
                            // Fallback to emrTypeResults
                            fieldType = emrTypeResults.find(result => 
                              result.emr_type_id === editingSession.emr_type_id && 
                              result.status === "confirmed" && 
                              result.key === fieldName
                            )?.type || 'text';
                          }
                        }
                        
                        // Handle client field specially - show client name instead of ID
                        if (fieldName === 'client') {
                          return (
                            <div key={fieldName}>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Client
                              </label>
                              <input
                                type="text"
                                value={editingSession?.client_id_name || editForm[fieldName] || ''}
                                onChange={(e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter client name"
                              />
                            </div>
                          );
                        }
                        
                        // Handle client_id field specially - ensure it shows custom data, not UUID
                        if (fieldName === 'client id' || fieldName === 'client_id') {
                          // Get the value from editForm (which should already be handled by our special logic)
                          const fieldValue = editForm[fieldName] || '';
                          return (
                            <div key={fieldName}>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                {fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                              </label>
                              <input
                                type="text"
                                value={fieldValue}
                                onChange={(e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder={`Enter ${fieldName.toLowerCase()}`}
                              />
                            </div>
                          );
                        }
                        

                        
                        // Use the renderFieldByType function for all other fields
                        return (
                          <div key={fieldName}>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              {fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </label>
                            {renderFieldByType(fieldName, editForm[fieldName], fieldType)}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* Instructions Field - Only show if there are confirmed fields */}
                {(() => {
                  const availableFields = [];
                  if (emrTypeResults && Array.isArray(emrTypeResults)) {
                    const confirmedFields = emrTypeResults.filter(result => 
                      result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
                    );
                    const fieldNames = confirmedFields.map(result => result.key);
                    const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
                      self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
                    );
                    availableFields.push(...uniqueFieldNames);
                  }
                  
                  // Only show Instructions if there are confirmed fields
                  if (availableFields.length === 0) {
                    return null;
                  }
                  
                  return (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="text-lg font-medium text-slate-700">Instructions</h4>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Instructions
                    </label>
                    <textarea
                      value={editForm.instructions || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, instructions: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="Enter session instructions"
                    />
                  </div>
                </div>
                  );
                })()}

              </div>

              {/* Error Message */}
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mt-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">{editError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSession(null);
                    setEditForm({});
                  }}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors"
                  disabled={savingSession}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSessionChanges}
                  disabled={savingSession}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    savingSession
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                  }`}
                >
                  {savingSession ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800">Delete Session</h3>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-slate-600 mb-4">
                Are you sure you want to delete this session? This action cannot be undone.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-slate-700 mb-1">Session Details:</p>
                <p className="text-xs text-slate-600">ID: {sessionToDelete?.id?.slice(0, 8)}...</p>
                <p className="text-xs text-slate-600">Type: {sessionToDelete?.emr_name}</p>
                <p className="text-xs text-slate-600">Created: {sessionToDelete?.created_at}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSessionToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={deletingSession}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSession}
                disabled={deletingSession}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingSession ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </div>
                ) : (
                  'Delete Session'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Session Modal */}
      {showNewSessionModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-30 pt-8">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] m-4">
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 4rem)' }}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-slate-700">Create New Session</h3>
                <button
                  onClick={() => {
                    setShowNewSessionModal(false);
                    setSelectedType('');
                    setSelectedClient('');
                    setSessionForm({
                      instructions: ''
                    });
                  }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <XMarkIcon className="w-6 h-4" />
                </button>
              </div>
              
              {/* Type Dropdown */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type *
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loadingEmrTypes}
                  required
                >
                  <option value="">
                    {loadingEmrTypes ? 'Loading EMR types...' : 'Select an EMR type'}
                  </option>
                  {emrTypes && Array.isArray(emrTypes) && emrTypes.map((type) => (
                    <option key={type?.id || 'unknown'} value={type?.id || ''}>
                      {safeString(type?.name)}
                    </option>
                  ))}
                </select>
                {emrTypesError && (
                  <p className="text-red-600 text-sm mt-1">{emrTypesError}</p>
                )}
              </div>

              {/* Client Dropdown - Pre-filled and disabled */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Client *
                </label>
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  disabled={true}
                  required
                >
                  <option value="">
                    {loadingClients ? 'Loading clients...' : 'Select a client'}
                  </option>
                  {clients && Array.isArray(clients) && clients.map((client) => (
                    <option key={client?.id || 'unknown'} value={client?.id || ''}>
                      {safeString(client?.first_name)} {safeString(client?.last_name)}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-slate-500 mt-1">Pre-filled with current client</p>
                {clientsError && (
                  <p className="text-red-600 text-sm mt-1">{clientsError}</p>
                )}
              </div>



              {/* Dynamic Fields based on EMR Type Results */}
              {(() => {
                console.log('=== DYNAMIC FIELDS DEBUG ===');
                console.log('selectedType:', selectedType);
                console.log('selectedType type:', typeof selectedType);
                console.log('emrTypeResults:', emrTypeResults);
                console.log('emrTypeResults length:', emrTypeResults?.length);
                console.log('========================');
                
                if (!selectedType) {
                  console.log('No type selected, not showing dynamic fields section');
                  return null;
                }
                
                return (
                <div className="space-y-4 border-t pt-4">
                    <h4 className="text-lg font-medium text-slate-700">Session Fields</h4>
                  {(() => {
                    const availableFields = [];
                    
                    // Get dynamic fields from emrTypeResults
                    if (emrTypeResults && Array.isArray(emrTypeResults)) {
                      const confirmedFields = emrTypeResults.filter(result => 
                        result.emr_type_id === selectedType && result.status === "confirmed"
                      );
                      // Get unique field names to avoid duplicates (case-insensitive)
                      const fieldNames = confirmedFields.map(result => result.key);
                      const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
                        self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
                      );
                      availableFields.push(...uniqueFieldNames);
                    }
                    
                    // Get manual fields
                    const typeManualFields = getManualFieldsForType(selectedType);
                    if (typeManualFields.length > 0) {
                      availableFields.push(...typeManualFields.map(field => field.name));
                    }
                    
                    console.log('Available fields (dynamic + manual) for this EMR type in create modal:', availableFields);
                    
                    // Show message if no confirmed fields available
                    if (availableFields.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                  </div>
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-sm">
                            <h3 className="text-xl font-bold text-amber-900 mb-3">No confirmed fields available</h3>
                            <p className="text-amber-700 text-base leading-relaxed">
                              You don't have any confirmed fields in the results table yet.
                              <br />
                              <span className="text-sm text-amber-600 mt-2 block">
                                Fields will appear here once they are confirmed in the EMR type analysis.
                              </span>
                            </p>
                  </div>
                    </div>
                      );
                    }
                    
                    return availableFields.map((fieldName) => {
                      // Get field type from EMR field definitions first, then fallback to manual fields or emrTypeResults
                      let fieldType = 'text';
                      
                      // First try to find in EMR field definitions
                      const emrField = emrFields.find(field => 
                        field.name.toLowerCase() === fieldName.toLowerCase()
                      );
                      if (emrField) {
                        fieldType = emrField.type;
                      } else {
                        // Try to find in manual fields
                        const manualField = getManualFieldsForType(selectedType).find(field => 
                          field.name.toLowerCase() === fieldName.toLowerCase()
                        );
                        if (manualField) {
                          fieldType = manualField.type;
                        } else {
                          // Fallback to emrTypeResults
                          fieldType = emrTypeResults.find(result => 
                            result.emr_type_id === selectedType && 
                            result.status === "confirmed" && 
                            result.key === fieldName
                          )?.type || 'text';
                        }
                      }
                      
                      // Skip client field as it's handled by the client dropdown
                      if (fieldName === 'client' || fieldName === 'client_id' || fieldName === 'client id') {
                        return null;
                      }
                      
                      return (
                        <div key={fieldName}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                            {fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </label>
                          {renderFieldByType(fieldName, sessionForm[fieldName], fieldType, (e) => {
                            const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
                            handleFormChange(fieldName, value);
                          })}
                  </div>
                      );
                    });
                  })()}
                </div>
                );
              })()}

              {/* Instructions Field - Common for all session types */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-lg font-medium text-slate-700">Instructions</h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Instructions
                  </label>
                  <textarea
                    value={sessionForm.instructions || ''}
                    onChange={(e) => handleFormChange('instructions', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Enter session instructions"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-6 border-t">
                <button
                  onClick={handleCreateSession}
                  disabled={!isFormValid || creatingSession}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isFormValid && !creatingSession
                      ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer' 
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  {creatingSession ? 'Creating...' : 'Create Session'}
                </button>
                <button
                  onClick={() => {
                    setShowNewSessionModal(false);
                    setSelectedType('');
                    setSelectedClient('');
                    setSessionForm({
                      instructions: ''
                    });
                  }}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fields to Display Modal */}
      {showFieldsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-blue-800">Select Fields to Display</h3>
                <button
                  onClick={() => setShowFieldsModal(false)}
                  className="text-blue-400 hover:text-blue-600 bg-white rounded-full p-1"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-0">
                {/* Available Fields */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700">Available Fields</h4>
                  <div className="border border-slate-200 rounded-lg h-64 overflow-y-auto">
                    {availableFields.map((field) => (
                      <div
                        key={field}
                        onClick={() => setSelectedAvailableField(field)}
                        className={`p-3 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                          selectedAvailableField === field ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <span className="text-sm text-slate-700">{formatFieldName(field)}</span>
                      </div>
                    ))}
                    {availableFields.length === 0 && (
                      <div className="p-3 text-sm text-slate-500 text-center">
                        No available fields
                      </div>
                    )}
                  </div>
                </div>

                {/* Movement Controls */}
                <div className="flex flex-col justify-center items-center">
                  <button
                    onClick={moveFieldToVisible}
                    disabled={!selectedAvailableField}
                    className={`p-1.5 rounded-t transition-colors border-b-0 ${
                      selectedAvailableField
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={moveFieldToAvailable}
                    disabled={!selectedVisibleField}
                    className={`p-1.5 rounded-b transition-colors border-t-0 ${
                      selectedVisibleField
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                </div>

                                {/* Visible Fields */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700">Visible Fields</h4>
                  <div className="flex items-start gap-2">
                    <div className="border border-slate-200 rounded-lg h-64 overflow-y-auto flex-1">
                      {visibleFields.map((field) => (
                        <div
                          key={field}
                          onClick={() => setSelectedVisibleField(field)}
                          className={`p-3 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                            selectedVisibleField === field ? 'bg-blue-50 border-blue-200' : ''
                          }`}
                        >
                          <span className="text-sm text-slate-700">{formatFieldName(field)}</span>
                        </div>
                      ))}
                      {visibleFields.length === 0 && (
                        <div className="p-3 text-sm text-slate-500 text-center">
                          No visible fields
                        </div>
                      )}
                    </div>
                    
                    {/* Reorder Controls - Positioned outside on the right side */}
                    <div className="flex flex-col justify-center items-center">
                      <button
                        onClick={moveFieldUp}
                        disabled={!selectedVisibleField || visibleFields.indexOf(selectedVisibleField) <= 0}
                        className={`p-1.5 rounded-t transition-colors border-b-0 ${
                          selectedVisibleField && visibleFields.indexOf(selectedVisibleField) > 0
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                        title="Move field up"
                      >
                        <ChevronUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={moveFieldDown}
                        disabled={!selectedVisibleField || visibleFields.indexOf(selectedVisibleField) >= visibleFields.length - 1}
                        className={`p-1.5 rounded-b transition-colors border-t-0 ${
                          selectedVisibleField && visibleFields.indexOf(selectedVisibleField) < visibleFields.length - 1
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                        title="Move field down"
                      >
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setShowFieldsModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveFieldDisplayConfig}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default ClientDetail; 